/**
 * Read-only display of the context auto-captured with a request
 * (help.html `.ctx`): page, role, org, app version, browser, and — when
 * the compose was opened from an error state — the error reference.
 *
 * The requester never sees this (context is still captured and sent
 * silently); it's shown only to platform admins reviewing a request.
 */

import styled from 'styled-components';
import type { SupportContext } from '@upskill/shared';

const Card = styled.div`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border: 1px dashed ${({ theme }) => theme.colors.borders.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing[3]};
`;

const Heading = styled.div`
  font-size: 10.5px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.light};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5px ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
`;

const Field = styled.div`
  min-width: 0;
  overflow-wrap: anywhere;

  span {
    color: ${({ theme }) => theme.colors.text.light};
  }
`;

const ErrorRow = styled.div`
  grid-column: 1 / 3;
  color: ${({ theme }) => theme.colors.status.error.text};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  overflow-wrap: anywhere;
`;

export interface AutoContextCardProps {
  context: SupportContext;
}

export function AutoContextCard({ context }: AutoContextCardProps) {
  return (
    <Card>
      <Heading>Attached automatically</Heading>
      <Grid>
        {context.errorRef && <ErrorRow>Error reference: {context.errorRef}</ErrorRow>}
        <Field>
          <span>Page: </span>
          {context.pageUrl}
        </Field>
        <Field>
          <span>Env: </span>
          {context.env}
        </Field>
        <Field>
          <span>Role: </span>
          {context.role}
        </Field>
        <Field>
          <span>Version: </span>
          {context.appVersion}
        </Field>
        <Field>
          <span>Browser: </span>
          {context.browser}
        </Field>
      </Grid>
    </Card>
  );
}

// __APP_VERSION__ is the build-time global from vite.config.ts's `define`
// (package.json's version), declared in vite-env.d.ts; `typeof` guard mirrors
// Sidebar.tsx's APP_VERSION so a context without the define still works.
// `role` is intentionally NOT captured here (ADO #542 item 8) — the server
// resolves the requester's real role and overwrites whatever the client
// sends, so a client-guessed value would only ever be misleading.

/** Captures the current page/env/version/browser context for a new request. */
export function captureSupportContext(errorRef?: string): SupportContext {
  return {
    pageUrl: window.location.pathname,
    env: (import.meta.env.VITE_APP_ENV ?? 'production').trim() || 'production',
    appVersion: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev',
    browser: navigator.userAgent,
    ...(errorRef ? { errorRef } : {}),
  };
}

export default AutoContextCard;
